/* eslint-disable react/prop-types */
import { useMemo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTable, usePagination, useGlobalFilter, useSortBy } from 'react-table';
import { Stack } from '@mui/material';
import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';
import SoftInput from 'components/SoftInput';
import SoftButton from 'components/SoftButton';
import TableCommon from 'components/TableCommon';

const monthOptions = [
  { label: 'Tháng', value: '' },
  ...Array.from({ length: 12 }).map((_, index) => {
    const month = String(index + 1).padStart(2, '0');
    return { label: `Tháng ${month}`, value: month };
  }),
];

function DataTable({
  table,
  pagination,
  isSorted,
  noEndBorder,
  query,
  paginationData,
  setPaginationData,
  loading,
  onApplyFilters,
  onResetFilters,
}) {
  const columns = useMemo(() => table.columns, [table]);
  const data = useMemo(() => table.rows, [table]);

  const tableInstance = useTable(
    { columns, data, initialState: { pageIndex: 0 } },
    useGlobalFilter,
    useSortBy,
    usePagination,
  );

  const { setPageSize } = tableInstance;

  useEffect(() => setPageSize(paginationData.size), [paginationData.size, setPageSize]);

  const [year, setYear] = useState(query?.year || String(new Date().getFullYear()));
  const [month, setMonth] = useState(query?.month || String(new Date().getMonth() + 1).padStart(2, '0'));
  const [search, setSearch] = useState(query?.searchBy || '');

  useEffect(() => {
    setYear(query?.year || String(new Date().getFullYear()));
    setMonth(query?.month || String(new Date().getMonth() + 1).padStart(2, '0'));
    setSearch(query?.searchBy || '');
  }, [query]);

  return (
    <>
      <SoftBox
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        alignItems="flex-start"
        p={3}
        pb={1}
      >
        <SoftBox lineHeight={1} width="100%">
          <Stack spacing={2} direction="row" flexWrap="wrap">
            <SoftBox display="flex" flexDirection="column">
              <SoftTypography component="label" variant="caption" fontWeight="bold" mb={0.5}>
                Năm
              </SoftTypography>
              <SoftBox width="8rem">
                <SoftInput
                  placeholder="YYYY"
                  value={year}
                  onChange={({ currentTarget }) => {
                    const next = currentTarget.value.replace(/\D/g, '').slice(0, 4);
                    setYear(next);
                  }}
                />
              </SoftBox>
            </SoftBox>

            <SoftBox display="flex" flexDirection="column">
              <SoftTypography component="label" variant="caption" fontWeight="bold" mb={0.5}>
                Tháng
              </SoftTypography>
              <select
                value={month}
                onChange={(e) => {
                  setMonth(e.target.value);
                }}
                style={{ width: 140, height: 40, borderRadius: 8, border: '1px solid #d2d6da', padding: '0 8px' }}
              >
                {monthOptions.map((item) => (
                  <option key={item.value || 'all'} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </SoftBox>

            <SoftBox display="flex" flexDirection="column">
              <SoftTypography component="label" variant="caption" fontWeight="bold" mb={0.5}>
                Số CTGS
              </SoftTypography>
              <SoftBox width="14rem">
                <SoftInput
                  placeholder="Search..."
                  value={search}
                  onChange={({ currentTarget }) => {
                    setSearch(currentTarget.value);
                  }}
                />
              </SoftBox>
            </SoftBox>

            <SoftBox display="flex" alignItems="flex-end">
              <Stack direction="row" spacing={1}>
                <SoftBox
                  component="button"
                  onClick={() => {
                    setPaginationData((prev) => ({ ...prev, page: 1 }));
                    onApplyFilters({
                      year,
                      month,
                      searchBy: search.trim(),
                    });
                  }}
                  disabled={loading}
                  sx={{
                    height: "39px",
                    padding: "0 16px",
                    borderRadius: 0,
                    border: "none",
                    backgroundColor: "#1a73e8",
                    color: "white",
                    cursor: "pointer",
                    fontWeight: "bold",
                    opacity: loading ? 0.5 : 1,
                    "&:hover": { backgroundColor: "#1557b0" },
                  }}
                >
                  Tải dữ liệu
                </SoftBox>
                <SoftBox
                  component="button"
                  onClick={() => {
                    const nextYear = String(new Date().getFullYear());
                    const nextMonth = String(new Date().getMonth() + 1).padStart(2, '0');
                    setYear(nextYear);
                    setMonth(nextMonth);
                    setSearch('');
                    setPaginationData((prev) => ({ ...prev, page: 1 }));
                    onResetFilters({
                      year: nextYear,
                      month: nextMonth,
                      searchBy: '',
                    });
                  }}
                  disabled={loading}
                  sx={{
                    height: "39px",
                    padding: "0 16px",
                    borderRadius: 0,
                    border: "1px solid #d2d6da",
                    backgroundColor: "white",
                    color: "#344767",
                    cursor: "pointer",
                    fontWeight: "bold",
                    opacity: loading ? 0.5 : 1,
                    "&:hover": { backgroundColor: "#f8f9fa" },
                  }}
                >
                  Reset
                </SoftBox>
              </Stack>
            </SoftBox>
          </Stack>
        </SoftBox>
      </SoftBox>

      <TableCommon
        loading={loading}
        query={query}
        setQuery={() => {}}
        paginationData={paginationData}
        setPaginationData={setPaginationData}
        table={table}
        isSorted={isSorted}
        noEndBorder={noEndBorder}
        pagination={pagination}
        tableInstance={tableInstance}
      />
    </>
  );
}

DataTable.defaultProps = {
  pagination: { variant: 'gradient', color: 'info' },
  isSorted: true,
  noEndBorder: false,
  onApplyFilters: () => {},
  onResetFilters: () => {},
};

DataTable.propTypes = {
  query: PropTypes.any,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  onApplyFilters: PropTypes.func,
  onResetFilters: PropTypes.func,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(['contained', 'gradient']),
    color: PropTypes.oneOf([
      'primary',
      'secondary',
      'info',
      'success',
      'warning',
      'error',
      'dark',
      'light',
    ]),
  }),
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
};

export default DataTable;
